import React, { useState, useEffect } from "react";
import { MessageSquare, Phone, Mail, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PremiumModal from "../ui/PremiumModal";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";
import { conversationsApi, messagesApi, twilioApi } from "../../services/api";
import { formatPhoneNumber } from "../../utils/formatPhone";
import { useUserDataContext } from "../../context/UserDataContext";

interface ComposeMessageModalProps {
  open: boolean;
  onClose: () => void;
  clientName: string;
  clientPhone?: string;
  clientEmail?: string;
  clientId: string;
}

const ComposeMessageModal: React.FC<ComposeMessageModalProps> = ({
  open,
  onClose,
  clientName,
  clientPhone,
  clientEmail,
  clientId,
}) => {
  const navigate = useNavigate();
  const { userData } = useUserDataContext();

  const defaultChannel = clientPhone ? "sms" : "email";
  const [channel, setChannel] = useState<"sms" | "email">(defaultChannel);
  const [fromNumber, setFromNumber] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Twilio numbers
  const [phoneNumbers, setPhoneNumbers] = useState<SelectOption[]>([]);
  const [loadingNumbers, setLoadingNumbers] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setChannel(clientPhone ? "sms" : "email");
      setFromNumber(null);
      setSubject("");
      setBody("");
      setSending(false);
      setSuccess(false);
      setError("");
    }
  }, [open, clientPhone]);

  // Fetch Twilio numbers when SMS is selected
  useEffect(() => {
    if (open && channel === "sms") {
      setLoadingNumbers(true);
      twilioApi
        .listNumbers()
        .then((res) => {
          const nums = (res.data.data || res.data || []).map((n: any) => ({
            value: n.phoneNumber || n.number,
            label: n.friendlyName || n.phoneNumber || n.number,
            description: n.phoneNumber || n.number,
          }));
          setPhoneNumbers(nums);
          if (nums.length === 1) {
            setFromNumber(nums[0].value);
          }
        })
        .catch(() => {
          setPhoneNumbers([]);
        })
        .finally(() => setLoadingNumbers(false));
    }
  }, [open, channel]);

  const recipient = channel === "sms" ? clientPhone : clientEmail;

  const handleSend = async () => {
    if (!recipient) return;
    if (!body.trim()) {
      setError("Message body is required.");
      return;
    }
    if (channel === "sms" && !fromNumber) {
      setError("Please select a phone number to send from.");
      return;
    }

    setSending(true);
    setError("");

    try {
      // 1. Check if a conversation exists for this client
      const convRes = await conversationsApi.list({ clientId, limit: 1 });
      const existing = convRes.data.data || convRes.data || [];
      let conversationId: string;

      if (existing.length > 0) {
        conversationId = existing[0]._id;
      } else {
        // 2. Create a new conversation
        const newConv = await conversationsApi.create({
          subject: `Messages with ${clientName}`,
          clientId,
          participants: [{ kind: "user", userId: userData._id }],
          primaryChannel: channel,
        });
        conversationId = newConv.data._id;
      }

      // 3. Send the message
      await messagesApi.send({
        conversationId,
        channel,
        to: recipient,
        body: body.trim(),
        ...(channel === "email" && subject.trim() ? { subject: subject.trim() } : {}),
      });

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1200);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const footer = (
    <div className="flex justify-end gap-3">
      <button
        onClick={onClose}
        className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
      >
        Cancel
      </button>
      <button
        onClick={handleSend}
        disabled={sending || success || !recipient || !body.trim()}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ backgroundColor: success ? "var(--color-accent)" : "var(--color-primary)" }}
        onMouseEnter={(e) => {
          if (!sending && !success) e.currentTarget.style.backgroundColor = "var(--color-primary-hover)";
        }}
        onMouseLeave={(e) => {
          if (!sending && !success) e.currentTarget.style.backgroundColor = "var(--color-primary)";
        }}
      >
        {sending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Sending...
          </>
        ) : success ? (
          "Sent!"
        ) : (
          <>
            <MessageSquare className="h-4 w-4" />
            Send
          </>
        )}
      </button>
    </div>
  );

  return (
    <PremiumModal
      open={open}
      onClose={onClose}
      icon={MessageSquare}
      title={`Send Message to ${clientName}`}
      footer={footer}
    >
      <div className="space-y-5">
        {/* Recipient display */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">To</label>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg text-sm text-slate-700">
              {channel === "sms" ? (
                <Phone className="h-3.5 w-3.5 text-slate-500" />
              ) : (
                <Mail className="h-3.5 w-3.5 text-slate-500" />
              )}
              {channel === "sms" ? formatPhoneNumber(recipient) || "No contact info available" : recipient || "No contact info available"}
            </span>
          </div>
        </div>

        {/* Channel toggle */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Channel</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => clientPhone && setChannel("sms")}
              disabled={!clientPhone}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                channel === "sms"
                  ? "text-white"
                  : "border border-slate-200 text-slate-600 hover:bg-slate-50"
              } ${!clientPhone ? "opacity-40 cursor-not-allowed" : ""}`}
              style={channel === "sms" ? { backgroundColor: "var(--color-primary)" } : {}}
            >
              <Phone className="h-4 w-4" />
              SMS
            </button>
            <button
              type="button"
              onClick={() => clientEmail && setChannel("email")}
              disabled={!clientEmail}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                channel === "email"
                  ? "text-white"
                  : "border border-slate-200 text-slate-600 hover:bg-slate-50"
              } ${!clientEmail ? "opacity-40 cursor-not-allowed" : ""}`}
              style={channel === "email" ? { backgroundColor: "var(--color-primary)" } : {}}
            >
              <Mail className="h-4 w-4" />
              Email
            </button>
          </div>
        </div>

        {/* From Number (SMS only) */}
        {channel === "sms" && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">From Number</label>
            {loadingNumbers ? (
              <div className="flex items-center gap-2 text-sm text-slate-400 py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading phone numbers...
              </div>
            ) : phoneNumbers.length > 0 ? (
              <SearchableSelect
                options={phoneNumbers}
                value={fromNumber}
                onChange={setFromNumber}
                placeholder="Select a phone number..."
                searchPlaceholder="Search numbers..."
                icon={Phone}
              />
            ) : (
              <div className="text-sm text-slate-500 py-2">
                No phone numbers configured.{" "}
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    navigate("/settings/phone-numbers");
                  }}
                  className="font-medium hover:underline"
                  style={{ color: "var(--color-primary)" }}
                >
                  Add a phone number
                </button>
              </div>
            )}
          </div>
        )}

        {/* Subject (Email only) */}
        {channel === "email" && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter subject..."
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
            />
          </div>
        )}

        {/* Body */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Message</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Type your message..."
            rows={6}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 resize-none transition-colors"
          />
          {channel === "sms" && (
            <p className="text-xs text-slate-400 mt-1.5">{body.length}/160 characters</p>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-2.5">{error}</div>
        )}
      </div>
    </PremiumModal>
  );
};

export default ComposeMessageModal;
